import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, LinearProgress, keyframes } from '@mui/material';
import { useTranslation } from 'react-i18next';

// Shimmer effect for completion celebration
const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

// Sparkle keyframes for particles
const sparkle = keyframes`
  0% {
    transform: scale(0) rotate(0deg);
    opacity: 1;
  }
  50% {
    transform: scale(1) rotate(180deg);
    opacity: 0.8;
  }
  100% {
    transform: scale(0) rotate(360deg);
    opacity: 0;
  }
`;

// Pulse glow effect
const pulseGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 5px currentColor, 0 0 10px currentColor;
  }
  50% {
    box-shadow: 0 0 15px currentColor, 0 0 25px currentColor, 0 0 35px currentColor;
  }
`;

// Sparkle particle component
function Sparkle({ delay, left, size }) {
  return (
    <Box
      sx={{
        position: 'absolute',
        left: `${left}%`,
        top: '50%',
        width: size,
        height: size,
        backgroundColor: 'warning.main',
        borderRadius: '50%',
        animation: `${sparkle} 0.8s ease-out ${delay}s forwards`,
        boxShadow: '0 0 6px currentColor',
        zIndex: 10,
      }}
    />
  );
}

export function AnimatedProgressBar({
  completedSteps,
  totalSteps,
  label,
}) {
  const { t } = useTranslation(['tasks']);
  const [sparkles, setSparkles] = useState([]);
  const isComplete = totalSteps > 0 && completedSteps >= totalSteps;
  const wasComplete = useRef(isComplete);
  const value = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;

  // Celebrate the moment the last step is answered, not every page load
  useEffect(() => {
    if (isComplete && !wasComplete.current) {
      setSparkles(Array.from({ length: 12 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 0.5,
        size: 4 + Math.random() * 6,
      })));
      const timeout = setTimeout(() => setSparkles([]), 1500);
      wasComplete.current = true;
      return () => clearTimeout(timeout);
    }
    wasComplete.current = isComplete;
    return undefined;
  }, [isComplete]);

  return (
    <Box sx={{ mb: 1, position: 'relative' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {label || t('tasks:procedure.progress', 'Fremskridt')}
        </Typography>
        <Typography variant="caption" color={isComplete ? 'success.main' : 'text.secondary'} fontWeight={600}>
          {completedSteps}/{totalSteps}
        </Typography>
      </Box>

      <Box sx={{ position: 'relative', overflow: 'visible' }}>
        {sparkles.map((s) => (
          <Sparkle key={s.id} left={s.left} delay={s.delay} size={s.size} />
        ))}

        <LinearProgress
          variant="determinate"
          value={value}
          color={isComplete ? 'success' : 'primary'}
          sx={{
            height: 6,
            borderRadius: 3,
            ...(sparkles.length > 0 && {
              '& .MuiLinearProgress-bar': {
                background: (theme) => `linear-gradient(
                  90deg,
                  ${theme.palette.info.main} 0%,
                  ${theme.palette.error.light} 25%,
                  ${theme.palette.warning.main} 50%,
                  ${theme.palette.success.light} 75%,
                  ${theme.palette.secondary.main} 100%
                )`,
                backgroundSize: '200% 100%',
                animationName: `${shimmer}, ${pulseGlow}`,
                animationDuration: '1.5s, 1.5s',
                animationIterationCount: '1, 1',
                animationTimingFunction: 'linear, ease-in-out',
              },
            }),
          }}
        />
      </Box>
    </Box>
  );
}
